import PageTitle from '@ui/PageTitle';
import TextField from '@ui/TextField';
import { Button, Typography } from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from '@app/store';
import { useLazyGetProfileQuery } from '@modules/account';
import COLORS from '@app/colors';

import { useLoginMutation } from '../../api/auth.api';
import { setToken, setUser } from '../../store/auth.slice';
import { ILoginFormValues } from '../../types';
import { LoginFormBox, LoginFormInputs } from './styled';
import LoginFormValidation from '../../helpers/loginFormValidation';
import usePasswordInput from '@hooks/usePasswordInput';
import { Container, ErrorText } from '../styled';

const LoginForm = () => {
  const {
    inputType,
    inputProps,
  } = usePasswordInput();

  const dispatch = useAppDispatch();

  const [loginRequest, { isLoading, isError }] = useLoginMutation();
  const [getProfile] = useLazyGetProfileQuery();

  const { handleSubmit, control, formState: { errors } } = useForm<ILoginFormValues>({
    resolver: yupResolver(LoginFormValidation),
    defaultValues: {
      login: '',
      password: '',
    },
  });

  const onSubmit: SubmitHandler<ILoginFormValues> = async (data) => {
    try {
      const { data: { accessToken } } = await loginRequest(data).unwrap();

      dispatch(setToken({ accessToken }));

      const { data: profileResponseData } = await getProfile().unwrap();

      dispatch(setUser({ user: profileResponseData }));
    } catch (e) {
      //
    }
  };

  return (
    <Container>
      <LoginFormBox component="form" onSubmit={handleSubmit(onSubmit)}>
        <PageTitle textAlign="center">Sign In to delete your account</PageTitle>

        <LoginFormInputs>
          <Controller
            name="login"
            control={control}
            render={({
              field: {
                onChange,
                onBlur,
                value,
                name,
              },
            }) => (
              <TextField
                label="Email or username"
                placeholder="Email or username"
                type="login"
                fullWidth
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors?.login?.message}
                helperText={errors?.login?.message}
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            render={({
              field: {
                onChange,
                onBlur,
                value,
              },
            }) => (
              <TextField
                label="Password"
                placeholder="Password"
                type={inputType}
                InputProps={inputProps}
                fullWidth
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors?.password?.message}
                helperText={errors?.password?.message}
              />
            )}
          />
        </LoginFormInputs>

        <ErrorText textAlign="center">
          {isError && 'Your credentials are invalid or the account is already deleted'}
        </ErrorText>

        <Button
          type="submit"
          fullWidth
          disabled={isLoading}
          variant="contained"
          color="success"
        >
          {isLoading ? 'Loading...' : 'Login'}
        </Button>

      </LoginFormBox>
    </Container>
  );
};

export default LoginForm;
